<template>
  <PageView
    :application="application"
    class="with-submenu"
  >
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="application.localName || application.title"
      role="navigation"
    />
    <TitleAndDescription
      :application="application"
      :local-description="currentAuthorization.description"
      :local-name="application.localName || application.title"
      :local-title="
        isNewAuth
          ? $t('titles.data-type-new-authorization')
          : currentAuthorization.name
      "
    />
    <div class="contentView">
      <Form ref="observer">
        <div class="columns">
          <Field
            v-slot="{ errors, valid, field }"
            class="column is-5"
            name="name"
            rules="required"
            vid="name"
          >
            <BField
              :label="$t('dataTypeAuthorizations.name')"
              :aria-label="$t('dataTypeAuthorizations.name')"
              :message="errors[0]"
              :type="{
                'is-danger': errors && errors.length > 0,
                'is-success': valid,
              }"
              class="column mb-4"
            >
              <b-input
                v-model="currentAuthorization.name"
                :value="currentAuthorization.name"
                :on-update:modelValue="currentAuthorization.name"
                :placeholder="$t('dataTypeAuthorizations.name-authorization-placeholder')"
                :aria-label="$t('dataTypeAuthorizations.name-authorization-placeholder')"
                @input="field.onChange($event)"
              />
            </BField>
          </Field>
          <Field
            v-slot="{ errors, valid, field }"
            class="column is-5"
            name="description"
            rules="required"
            vid="description"
          >
            <BField
              :label="$t('applications.comment')"
              :aria-label="$t('applications.comment')"
              :message="errors[0]"
              :type="{
                'is-danger': errors && errors.length > 0,
                'is-success': valid,
              }"
              class="column mb-4"
            >
              <BInput
                v-model="currentAuthorization.description"
                :value="currentAuthorization.description"
                :on-update:value="currentAuthorization.description"
                :on-update:modelValue="currentAuthorization.description"
                :placeholder="$t('dataTypeAuthorizations.comment-authorization-placeholder')"
                :aria-label="$t('dataTypeAuthorizations.comment-authorization-placeholder')"
                maxlength="200"
                type="textarea"
                @input="field.onChange($event)"
              />
            </BField>
          </Field>
        </div>
      </Form>
      <AuthorizationTableForDatatype
        :application="application"
        :current-authorization="currentAuthorization"
        :authorization-id="authorizationId"
        :authorizations="authorizations"
        :datatypes="datatypes"
        :references="references"
        :has-dependencies="hasDependencies"
        :initialized="initialized"
        :application-name="applicationName"
        :references-scopes="referencesScopes"
        :has-public-authorizations="hasPublicAuthorizations"
        @update:authorization="updateAuthorization"
      />
      <div class="buttons columns">
        <BButton
          v-if="currentAuthorization.name && currentAuthorization.description"
          class="column is-offset-8 is-2"
          :icon-left="isNewAuth ? 'plus' : 'save'"
          :outlined="!isNewAuth"
          rounded
          expanded
          :type="isNewAuth ? 'is-dark' : 'is-warning'"
          @click="createOrUpdateAuthorization"
        />
        <BNotification
          v-else
          aria-close-label="Close notification"
          type="is-info is-light"
          :aria-label="$t('dataTypeAuthorizations.notification-missing-info')"
        >
          {{ $t("dataTypeAuthorizations.notification-missing-info") }}
        </BNotification>
      </div>
    </div>
  </PageView>
</template>

<script lang="ts">
import PageView from "../../common/PageView.vue";
import SubMenu from "@/components/common/SubMenu.vue";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import { Form, Field } from "vee-validate";
import { onMounted, provide, ref, computed, onUpdated, watch } from "vue";
import useArray from "@/composable/components/array";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import services from "@/composable/services";
import { dataLoader } from "@/composable/data/dataLoader";
import AuthorizationTableForDatatype from "@/components/common/AuthorizationTableForDatatype.vue";
import TitleAndDescription from "@/components/common/TitleAndDescription.vue";
import { allDataName, createListData } from "@/composable/application/data";
import { BButton, BField, BInput, BNotification } from "buefy";
import "@/style/views/authorization/data/DataTypeAuthorizationInfoView.scss";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { ErrorType } from "@/composable/application/errors";
import { HttpStatusCodes } from "@/utils/HttpUtils";
import type { VeeForm } from "@/types/vee-validate";
import { useFieldErrorReset } from "@/composable/forms/useFieldErrorReset";
import { extractServerErrorCode } from "@/composable/errors/extractServerErrorCode";
import { LocalNode } from "@/types/front/LocalNode";
import {
  AuthorizationParsed,
  Configuration, CreateAuthorizationRequest,
  GetAuthorizationResult,
  StandardDataDescription,
  User,
} from "@/types/generated/UsedGeneratedTypes";
import useObject from "@/composable/components/object";

export default {
  name: "DataTypeAuthorizationInfoView",
  components: {
    BNotification,
    BButton,
    BField,
    BInput,
    TitleAndDescription,
    AuthorizationTableForDatatype,
    PageView,
    SubMenu,
    Form,
    Field,
  },
  props: {
    applicationName: {
      type: String,
      required: true,
    },
    authorizationId: {
      type: String,
      default: "new",
    },
    users: Array,
  },
  emits: ["update:infoAuth"],
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();
    const loader = dataLoader(services);
    provide("reference:dataLoader", loader);
    const { reactiveObject: application, doChangeObject: changeApplication } = useObject(new LocalApplicationResult());
    const datas = ref({} as { [p: string]: StandardDataDescription });
    const datatypes = ref({ withScope: {}, withoutScope: {} });
    const references = ref({ withScope: {}, withoutScope: {} });
    const referencesScopes = ref({});
    const initialized = ref(false);
    const { reactiveObject: configuration, doChangeObject: changeConfiguration } = useObject({} as Configuration);
    const selectedUsers = ref([] as User[] | GetAuthorizationResult[] | any[]);
    const { reactiveObject: authorizations } = useObject();
    const hasPublicAuthorizations = ref(false);
    const isLoading = ref(true);
    const { reactiveObject: currentAuthorization, doChangeObject: changeCurrentAuthorization } = useObject({} as GetAuthorizationResult);
    const dependencies = ref({});
    // Ref vers la balise {@code <Form ref="observer">} de vee-validate
    // ( bound via le template ) . Permet d'invoquer {@code setFieldError}
    // pour marquer le champ "name" en rouge depuis une erreur metier
    // serveur ( ex AUTHORIZATION_NAME_EXISTS 422 ) , et de l'effacer
    // automatiquement au retype via {@link useFieldErrorReset} .
    const observer = ref<VeeForm | null>(null);
    useFieldErrorReset(
        computed(() => currentAuthorization.name ?? ""),
        observer,
        "name",
    );
    const isNewAuth = window.location.href.split("/").includes("new");
    const isPublicAuth = window.location.href.split("/").includes("public");
    // #472 - Supprimé nameCurrentAuthorization et descriptionCurrentAuthorization
    // car ils étaient initialisés avant la résolution de la promesse async,
    // donc toujours vides, et écrasaient le nom/commentaire dans updateAuthorization().
    const userPublic = [
      {
        id: "9032ffe5-bfc1-453d-814e-287cd678484a",
        label: "_public_",
      },
    ];
    const buildDependencies = (hierarchicalNodes: any) => {
      const dependenciesObject: any = {};

      // Initialiser l'objet avec tous les datatypes
      hierarchicalNodes.forEach((node: LocalNode) => {
        dependenciesObject[node.nodeName] = {
          dependencies: [],
          dependents: [],
        };
      });

      // Remplir les dépendances et les dépendants
      hierarchicalNodes.forEach((node: LocalNode) => {
        // Ajouter les dépendances
        if (node.depends && node.depends.length > 0) {
          dependenciesObject[node.nodeName].dependencies = node.depends;

          // Ajouter ce datatype comme dépendant pour chacune de ses dépendances
          node.depends.forEach((dep) => {
            if (dependenciesObject[dep]) {
              dependenciesObject[dep].dependents.push(node.nodeName);
            }
          });
        }

        // Gérer la relation parent-enfant
        if (node.parent) {
          dependenciesObject[node.nodeName].dependencies.push(node.parent);
          if (dependenciesObject[node.parent]) {
            dependenciesObject[node.parent].dependents.push(node.nodeName);
          }
        }

        // Gérer les enfants
        if (node.children && node.children.length > 0) {
          node.children.forEach((childName: any) => {
            if (dependenciesObject[childName]) {
              dependenciesObject[childName].dependencies.push(node.nodeName);
              dependenciesObject[node.nodeName].dependents.push(childName);
            }
          });
        }
      });

      return dependenciesObject;
    };
    type AuthorizationWithRestriction = {
      operationTypes: string[];
      requiredAuthorizations?: AuthorizationParsed["requiredAuthorizations"];
      timeScope?: {
        fromDay?: number[];
        toDay?: number[];
        format?: string;
      };
    };
    watch(
      () => props.users,
      (newVal: any) => {
        if (isPublicAuth) {
          selectedUsers.value = userPublic;
        }
        else if (!isNewAuth) {
          selectedUsers.value = newVal || [];
        }
        else {
          selectedUsers.value = [];
          const authenticatedUser: any = services.loginService.getStoredAuthenticatedUser();
          if (authenticatedUser) {
            selectedUsers.value.push(authenticatedUser);
          }
        }
      },
    );
    watch(
      () => currentAuthorization.users,
      (newVal) => {
        if (isPublicAuth) {
          selectedUsers.value = userPublic;
        }
        else if (!isNewAuth) {
          selectedUsers.value = newVal || [];
        }
        else {
          selectedUsers.value = [];
          const authenticatedUser: any = services.loginService.getStoredAuthenticatedUser();
          if (authenticatedUser) {
            selectedUsers.value.push(authenticatedUser);
          }
        }
      },
    );

    onMounted(async () => {
      await init();
      changeSubMenuPaths([
        new SubMenuPath(
          t("menu.accueil").toLowerCase(),
          () => router.push(`/applications/${props.applicationName}`),
          () => router.push("/applications"),
        ),
        new SubMenuPath(
          t("dataTypeAuthorizations.sub-menu-data-type-authorizations"),
          () => {
            router.push(`/applications/${props.applicationName}/authorizations`);
          },
          () => router.push(`/applications/${props.applicationName}`),
        ),
        new SubMenuPath(
          props.authorizationId === "new" ? t("dataTypeAuthorizations.sub-menu-new-authorization") : t("dataTypeAuthorizations.modify"),
          () => {},
          () => {
            router.push(`/applications/${props.applicationName}/authorizations`);
          },
        ),
      ]);
    });

    onUpdated(() => {
      if (isPublicAuth) {
        selectedUsers.value = userPublic;
      }
      else if (!isNewAuth && currentAuthorization.users) {
        selectedUsers.value = currentAuthorization.users;
      }
      else if (!isNewAuth && props.users) {
        selectedUsers.value = props.users;
      }
      else {
        selectedUsers.value = [];
        const authenticatedUser: any = services.loginService.getStoredAuthenticatedUser();
        if (authenticatedUser) {
          selectedUsers.value.push(authenticatedUser);
        }
      }
    });

    async function init() {
      try {
        const getApplication = await services.applicationService.getApplication(
          props.applicationName,
          ["CONFIGURATION", "DATATYPE"],
        );
        if (getApplication) {
          changeApplication(getApplication);
        }
        datas.value = Object.keys(application.data).reduce((acc: any, data: string) => {
          acc[data] = {
            id: data,
            name:
              services.internationalisationService.localeReferenceNames(data, application)
              || data,
          };
          return acc;
        }, {});
        let listDataNodeNames: string[] = [];
        for (let localDataName in application.dataTypes) {
          listDataNodeNames = allDataName(
            application.dataTypes[localDataName],
            listDataNodeNames,
          );
        }
        datatypes.value = createListData(listDataNodeNames, application);
        let listReferenceNodeNames: string[] = [];
        for (let localDataName in application.references) {
          listReferenceNodeNames = allDataName(
            application.references[localDataName],
            listReferenceNodeNames,
          );
        }
        references.value = createListData(listReferenceNodeNames, application);
        changeConfiguration(application.configuration);
        dependencies.value = buildDependencies(configuration.hierarchicalNodes);
        for (let data of Object.keys(configuration.dataDescription)) {
          if (configuration?.dataDescription?.[data]?.authorization) {
            authorizations[data] = configuration.dataDescription[data].authorization;
          }
        }
      }
      catch (error) {
        services.alertService.toastServerError(error as ErrorType);
      }
      finally {
        isLoading.value = false;
      }

      let listAllUsers = await services.authorizationService.getAuthorizationGrantableInfos(
        props.applicationName,
      );
      console.log("listAllUsers.referenceScopes", listAllUsers.referenceScopes);
      console.log(
        "listAllUsers.referenceScopes",
        Object.keys(listAllUsers.referenceScopes || {}).reduce((acc: any, dataName: string) => {
          if (listAllUsers.referenceScopes[dataName].length) {
            acc[dataName] = listAllUsers.referenceScopes[dataName];
          }
          return acc;
        }, {}),
      );
      let scopes = listAllUsers.referenceScopes;
      referencesScopes.value = Object.keys(scopes || {}).reduce((acc: any, dataName: string) => {
        if (scopes[dataName].length) {
          acc[dataName] = scopes[dataName];
        }
        return acc;
      }, {});
      if (!isNewAuth) {
        services.authorizationService
          .getAdminAuthorizationsForOpenAdom(props.applicationName, props.authorizationId)
          .then((authorization: any) => {
            changeCurrentAuthorization(authorization);
            initialized.value = true;
          });
        // #472 - Les lignes qui assignaient nameCurrentAuthorization et descriptionCurrentAuthorization
        // ont été supprimées : elles s'exécutaient avant la résolution du .then() ci-dessus,
        // donc currentAuthorization.name était encore undefined à ce moment-là.
      }
      else {
        changeCurrentAuthorization({});
        initialized.value = true;
        if (isPublicAuth) {
          hasPublicAuthorizations.value = true;
          selectedUsers.value = userPublic;
        }
        else {
          selectedUsers.value = [];
          const authenticatedUser: any = services.loginService.getStoredAuthenticatedUser();
          if (authenticatedUser) {
            selectedUsers.value.push(authenticatedUser);
          }
        }
      }
    }

    async function createOrUpdateAuthorization() {
      try {
        const authorizations: CreateAuthorizationRequest | any = buildAuthorization();
        // {@code await} obligatoire : sans lui , les rejets HTTP se
        // propageaient hors du try/catch et le {@code router.push} ci-dessous
        // s'executait meme en cas d'echec , masquant tous les retours metier .
        if (isNewAuth || (isPublicAuth && isNewAuth)) {
          await services.authorizationService.createAuthorization(props.applicationName, authorizations);
        }
        else {
          await services.authorizationService.updateAuthorization(props.applicationName, authorizations);
        }
        router.push(`/applications/${props.applicationName}/authorizations`);
      }
      catch (e) {
        await handleAuthorizationError(e as ErrorType);
      }
    }

    /**
     * Centralise le traitement des erreurs HTTP du flow create / update
     * d'autorisation . Aujourd'hui une seule cause metier connue
     * ( AUTHORIZATION_NAME_EXISTS 422 ) ; structure prete a accueillir
     * d'autres codes ( ex EXISTING_REFERENCE ) sans refondre le caller -
     * il suffit d'ajouter une branche {@code if (code === "...") { ... }}
     * ci-dessous . L'extraction du code est deleguee au helper partage
     * {@link extractServerErrorCode} pour eviter la duplication avec les
     * autres flows ( UserRegister , useEmailChangeFlow ) .
     */
    async function handleAuthorizationError(err: ErrorType): Promise<void> {
      if (err?.httpResponseCode === HttpStatusCodes.UNPROCESSABLE_ENTITY) {
        const code = await extractServerErrorCode(err);
        if (code === "AUTHORIZATION_NAME_EXISTS") {
          const msg = t("exceptionMessage.AUTHORIZATION_NAME_EXISTS");
          // Toast rouge ( visibilite immediate ) + setFieldError sur le
          // champ name ( bordure rouge + texte rouge sous le champ , reset
          // automatique au retype via useFieldErrorReset ) .
          services.alertService.toastErrorMessage(msg);
          observer.value?.setFieldError("name", msg);
          return;
        }
      }
      services.alertService.toastServerError(err);
    }

    function updateAuthorization(authorizations: any) {
      // #472 - Mettre à jour uniquement les autorisations sans toucher au nom et commentaire.
      // On ne peut PAS utiliser changeCurrentAuthorization() ( doChangeObject ) ici car il
      // ne conserve que les propriétés dont la valeur a changé - or name et description
      // n'ont pas changé, donc ils seraient supprimés de l'objet réactif.
      currentAuthorization.authorizations = authorizations;
    }

    const hasDependencies = computed(() => {
      const result = new Set();
      const authorizations = currentAuthorization.authorizations;
      const hierarchicalNodes = configuration.hierarchicalNodes;

      for (const [datatype, auth] of Object.entries(authorizations || {})) {
        if (auth.operationTypes && auth.operationTypes.length > 0) {
          // Trouver le nœud correspondant dans la configuration
          const node = hierarchicalNodes.find(n => n.nodeName === datatype);
          if (node && node.depends && node.depends.length > 0) {
            // Ajouter toutes les dépendances du datatype
            node.depends.forEach(dep => result.add(dep));
          }
        }
      }

      return Array.from(result);
    });
    const buildAuthorization = () => {
      const authorization: Record<string, AuthorizationParsed> = {
        ...currentAuthorization.authorizations,
      };
      const authorizationForAll: Record<string, Set<string> | string[]> = {};
      const authorizationsWithRestriction: Record<string, AuthorizationWithRestriction> = {};
      for (const [datatype, auth] of Object.entries(authorization)) {
        // Ticket #521 - réponse Damien 2026-05-25 : on n'envoie au backend
        // QUE les datatypes/refs ayant au moins une case cochée par
        // l'utilisateur . Le backend remplace ensuite entièrement le set
        // ( cf {@code AuthorizationService.addAuthorization}
        // ligne {@code entity.setAuthorizations(...)} ) , donc tout ce
        // qui n'est pas dans le payload est supprimé en base . C'est le
        // contrat WYSIWYG attendu : ce que l'user voit dans l'UI = ce
        // qui est en base après save .
        //
        // <p>Ancien filtre retiré : {@code operationTypes === ["extraction"]
        // && hasDependencies.includes(datatype)} qui skippait les entries
        // "extraction seule + ref dépendant" . Cela créait une incohérence
        // de persistance : ces entries restaient en base après décochage
        // de toutes les opérations parentes , parce que le mécanisme
        // backend {@code addDependantAuthorizations} ré-injectait les
        // dépendances en plus du payload utilisateur . Conséquence
        // observée : décocher tout côté UI laissait des extractions en
        // base au reload .
        //
        // Le mécanisme cross-datatype {@code hasDependencies} reste actif
        // côté UI ( extraction forcée + verrouillée sur les refs dépendants
        // tant qu'un datatype principal a une opération ) et côté backend
        // ( {@code addDependantAuthorizations} ré-injecte les extractions
        // pour les refs encore dépendants ) , donc aucune régression
        // fonctionnelle sur ce mécanisme .
        if (!auth.operationTypes || auth.operationTypes.length === 0) {
          continue;
        }
        auth.operationTypes = [...auth.operationTypes];
        const isWithTimeScope = datatype in datatypes.value.withScope;
        if (!isWithTimeScope) {
          authorizationForAll[datatype] = auth.operationTypes;
          continue;
        }
        const isInfiniteTimeScope
          = (!auth.fromDay && !auth.toDay)
            || (
              auth.fromDay?.includes(-999999999)
              && auth.toDay?.includes(999999999)
            );
        const hasNoRequiredAuths
          = !auth.requiredAuthorizations
            || Object.values(auth.requiredAuthorizations).every(
              (arr: any) => arr.length === 0 || (arr.length === 1 && arr[0] === ""),
            );
        if (isInfiniteTimeScope && hasNoRequiredAuths) {
          authorizationForAll[datatype] = new Set(auth.operationTypes);
        }
        else {
          const timeScope: AuthorizationWithRestriction["timeScope"] = {};
          if (auth.fromDay) {
            timeScope.fromDay = auth.fromDay;
            timeScope.format = auth.format;
          }
          if (auth.toDay) {
            timeScope.toDay = auth.toDay;
            timeScope.format = auth.format;
          }
          authorizationsWithRestriction[datatype] = {
            operationTypes: auth.operationTypes,
            requiredAuthorizations: auth.requiredAuthorizations,
            ...(Object.keys(timeScope).length && { timeScope }),
          };
        }
      }

      return {
        uuid: props.authorizationId === "new" ? null : props.authorizationId,
        name: currentAuthorization.name,
        description: currentAuthorization.description,
        usersId: (selectedUsers.value || []).map(user => user.id),
        authorizationForAll,
        authorizationsWithRestriction,
      };
    };

    return {
      observer,
      createOrUpdateAuthorization,
      updateAuthorization,
      application,
      datatypes,
      configuration,
      authorizations,
      selectedUsers,
      isLoading,
      references,
      datas,
      subMenuPaths,
      referencesScopes,
      currentAuthorization,
      initialized,
      hasDependencies,
      buildAuthorization,
      hasPublicAuthorizations,
      isNewAuth,
    };
  },
};
</script>
